/**
 * DRPX Courier — Profile-CRUD (Supabase)
 *
 * Lese- und Schreib-Operationen auf der `profiles`-Tabelle und der
 * `profiles_public`-View (mit DB-seitigem Schutz sensibler Felder).
 *
 * @module lib/supabase/profiles
 */

import { _sb } from './client.js';
import { mapProfileFromDb } from './mappers.js';
import { camelToSnake } from '../mapper.js';
import type { Profile, HeimatGeo, Favorit, BlockedUser } from '../../types.js';

// ════════════════════════════════════════════════════════════════════
// Interne Helper
// ════════════════════════════════════════════════════════════════════

/**
 * Einheitliche Fehler-Instanz, wenn der Supabase-Client nicht initialisiert
 * ist (z.B. wenn die .env-Variablen fehlen).
 */
const NO_CLIENT_ERROR = new Error('Supabase client not initialized');

// ════════════════════════════════════════════════════════════════════
// Lokale Interfaces (nicht exportiert)
// ════════════════════════════════════════════════════════════════════

/**
 * Input-Shape für `updateProfile`.
 *
 * Erweitert `Profile` um Felder, die heute in `updateProfile` akzeptiert
 * werden, aber noch nicht im zentralen `Profile`-Interface stehen (waren
 * in der ursprünglichen `types.js`-JSDoc-Doku nicht enthalten). Bei der
 * späteren Migration der konsumierenden Module wandern diese Felder nach
 * `types.ts`.
 */
interface ProfileUpdateInput extends Partial<Profile> {
  loginName?: string;
  gesperrt?: boolean;
  aboGutschriftGrund?: string | null;
  kycGeprueftAm?: string | null;
  kycGewerbeschein?: string | null;
  kycVersicherung?: string | null;
  kycBonitaetsnachweis?: string | null;
  fahrzeugTyp?: string;
  einsatzgebietGeo?: HeimatGeo | null;
  einsatzgebietRadius?: number;
  lastActive?: string | null;
  avvAkzeptiertAm?: string | null;
}

// ════════════════════════════════════════════════════════════════════
// Update-Whitelist
// ════════════════════════════════════════════════════════════════════

/**
 * Whitelist der Felder, die per `updateProfile` geändert werden dürfen.
 *
 * Sicherheits-Schutzschicht: verhindert, dass Caller versehentlich
 * geschützte Felder wie `id`, `email`, `created_at`, `stripeCustomerId`,
 * `nutzerNr` etc. überschreiben können (RLS in der DB ist die zweite
 * Schutzschicht).
 *
 * `as const satisfies readonly (keyof ProfileUpdateInput)[]` validiert
 * jeden Eintrag compile-time gegen das Input-Interface — Tippfehler oder
 * veraltete Feldnamen werden sofort erkannt.
 */
const PROFILE_UPDATE_WHITELIST = [
  'rolle',
  'firma',
  'inhaber',
  'strasse',
  'land',
  'plz',
  'ort',
  'ustId',
  'steuernr',
  'finanzamt',
  'anrede',
  'nachname',
  'vorname',
  'telefon',
  'telefax',
  'loginName',
  'notfallnr',
  'homepage',
  'gesperrt',
  'darfBieten',
  // DRPX-Pro (Migration 141): Admin schaltet das Team frei (DB: protect_admin_fields)
  'aboTier',
  'bonitaetGeprueft',
  'aboAktiv',
  'kycStatus',
  'transportAnweisungen',
  'aboInterval',
  'aboStartAm',
  'gekuendigtAm',
  'aboEndetAm',
  'aboPausiert',
  'aboPausiertBis',
  'aboGesperrt',
  'aboGutschrift',
  'aboGutschriftGrund',
  'aboErstattung',
  'probeStart',
  'kycGeprueftAm',
  'kycGewerbeschein',
  'kycVersicherung',
  'kycBonitaetsnachweis',
  'dokReviews',
  'favoriten',
  'blockedUsers',
  'kleinunternehmer',
  'iban',
  'bic',
  'bankName',
  'kennzeichen',
  'fahrzeugTyp',
  'ausstattung',
  'einsatzgebietGeo',
  'einsatzgebietRadius',
  'lastActive',
  'notifPrefs',
  'zertReviews',
  'adminNotiz',
  'adminMessages',
  'avvAkzeptiertAm',
] as const satisfies readonly (keyof ProfileUpdateInput)[];

// ════════════════════════════════════════════════════════════════════
// PROFILES
// ════════════════════════════════════════════════════════════════════

/**
 * Lädt ein einzelnes User-Profil.
 */
export async function getProfile(
  id: string,
): Promise<{ data: Profile | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.from('profiles').select('*').eq('id', id).single();
    return {
      data: r.data ? mapProfileFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Lädt die Firmendaten eines aktiven Mitarbeiter-Zugangs (DRPX-Pro,
 * Migration 139). Ein Mitarbeiter darf das Firmenprofil per RLS nicht lesen;
 * die RPC liefert eine feste Auswahl ohne Bank-, Steuer-, Stripe- und
 * KYC-Daten. Für alle anderen Konten — und für einen nicht aktiven
 * Mitarbeiter — ist `data` null.
 *
 * @returns Firmenprofil (nur die freigegebenen Felder) oder null
 */
export async function getMeineFirma(): Promise<{ data: Profile | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client.rpc('get_meine_firma');
    if (r.error) return { data: null, error: r.error };
    const row = r.data as Record<string, unknown> | null;
    return { data: row ? mapProfileFromDb(row) : null, error: null };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

/**
 * Lädt alle Profile mit eingeschränkten Spalten (kein IBAN, kein Stripe,
 * keine Admin-Notizen). Liest aus der `profiles_public`-View — sensitive
 * Felder sind DB-seitig geschützt.
 */
export async function getAllProfiles(): Promise<{
  data: Profile[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('profiles_public')
      .select('*')
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const mapped = rows
      .map((row) => mapProfileFromDb(row))
      .filter((row): row is Profile => row !== null);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Lädt alle Profile mit ALLEN Spalten — inklusive sensibler Daten wie
 * IBAN, Stripe-IDs und Admin-Notizen. NUR FÜR ADMINS (RLS prüft das DB-
 * seitig).
 */
export async function getAllProfilesFull(): Promise<{
  data: Profile[];
  error?: unknown;
}> {
  if (!_sb) return { data: [], error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const r = await client
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });
    const rows = (r.data || []) as Array<Record<string, unknown>>;
    const mapped = rows
      .map((row) => mapProfileFromDb(row))
      .filter((row): row is Profile => row !== null);
    return { data: mapped };
  } catch (e: unknown) {
    return { data: [], error: e };
  }
}

/**
 * Aktualisiert ein User-Profil. Akzeptiert nur Felder aus
 * `PROFILE_UPDATE_WHITELIST` und konvertiert camelCase → snake_case
 * automatisch via `camelToSnake`.
 *
 * Felder außerhalb der Whitelist werden stillschweigend ignoriert.
 * Das ist Absicht: verhindert versehentliches Überschreiben geschützter
 * Felder wie `id`, `email`, `stripeCustomerId` etc.
 */
export async function updateProfile(
  id: string,
  updates: ProfileUpdateInput,
): Promise<{ data: Profile | null; error: unknown }> {
  if (!_sb) return { data: null, error: NO_CLIENT_ERROR };
  const client = _sb;
  try {
    const d: Record<string, unknown> = {};
    for (const key of PROFILE_UPDATE_WHITELIST) {
      if (updates[key] !== undefined) {
        d[camelToSnake(key)] = updates[key];
      }
    }
    const r = await client.from('profiles').update(d).eq('id', id).select().single();
    return {
      data: r.data ? mapProfileFromDb(r.data as Record<string, unknown>) : null,
      error: r.error,
    };
  } catch (e: unknown) {
    return { data: null, error: e };
  }
}

// Kündigen läuft seit Lieferung 3b-2 (19.10.2026) im Stripe-Kundenportal
// (misc.ts → createPortalSession). Die alte RPC `cancel_subscription()`
// (Migration 070, Kündigungsfrist 1 Monat + Erstattung) ist seit
// Migration 146 für die App gesperrt; ihre Aufruf-Funktion ist entfernt.

// ════════════════════════════════════════════════════════════════════
// RPC: SELF-RESET OF REVIEWS (Audit 089 #1+#4, Migration 082)
// ════════════════════════════════════════════════════════════════════

/**
 * Entfernt den eigenen `zert_reviews[zertKey]`-Eintrag aus dem Profil.
 * Wird beim Re-Upload eines Zertifikats aufgerufen — die alte Admin-
 * Review ist nach einem neuen Upload obsolet.
 *
 * Hintergrund (Audit 089 #4):
 * Der `protect_admin_fields`-Trigger blockt direkte UPDATEs auf
 * `profiles.zert_reviews` durch den User. Diese RPC umgeht das via
 * SECURITY DEFINER, prüft aber server-seitig, dass für `(auth.uid(),
 * zertKey)` ein Eintrag in `fahrer_zertifikate` mit gefülltem
 * `file_path` existiert — ohne tatsächlichen Upload kein Reset.
 *
 * @see src/sql/audit-day-2026-05-18/082_self_reset_reviews_rpc.sql
 */
export async function resetZertReviewForSelf(
  zertKey: string,
): Promise<{ error?: string }> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.rpc('reset_zert_review_for_self', {
      p_zert_key: zertKey,
    });
    if (r.error) return { error: r.error.message };
    return {};
  } catch (e: unknown) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Markiert eine (msgId) oder alle (ohne Argument) eigenen Admin-Nachrichten
 * als gelesen. SECURITY-DEFINER-RPC (Migration 100), weil protect_admin_fields
 * direkte admin_messages-Updates durch User blockiert.
 *
 * **Aufruf-Pattern:** über `apiSilent()` (Hintergrund-Persist des Glocken-Dropdowns).
 */
export async function adminMsgMarkGelesen(msgId?: string): Promise<{ error?: string }> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.rpc('admin_msg_mark_gelesen', {
      p_msg_id: msgId ?? null,
    });
    if (r.error) return { error: r.error.message };
    return {};
  } catch (e: unknown) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Löscht eine eigene Admin-Nachricht endgültig (Papierkorb im
 * Glocken-Dropdown). SECURITY-DEFINER-RPC (Migration 100).
 *
 * **Aufruf-Pattern:** über `apiSilent()`.
 */
export async function adminMsgDelete(msgId: string): Promise<{ error?: string }> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.rpc('admin_msg_delete', { p_msg_id: msgId });
    if (r.error) return { error: r.error.message };
    return {};
  } catch (e: unknown) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Entfernt den eigenen `dok_reviews[docKey]`-Eintrag aus dem Profil.
 * Wird beim Re-Upload eines KYC-Dokuments aufgerufen.
 *
 * Hintergrund (Audit 089 #1 follow-up):
 * Analog zu `resetZertReviewForSelf`. Erlaubte Keys server-seitig:
 * 'gewerbeschein', 'versicherung', 'bonitaetsnachweis' (Migration 111).
 * Erfordert dass das jeweilige
 * `profiles.kyc_<docKey>`-Feld gefüllt ist (= echter Upload).
 *
 * @see src/sql/audit-day-2026-05-18/082_self_reset_reviews_rpc.sql
 */
export async function resetDokReviewForSelf(
  docKey: 'gewerbeschein' | 'versicherung' | 'bonitaetsnachweis',
): Promise<{ error?: string }> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.rpc('reset_dok_review_for_self', {
      p_doc_key: docKey,
    });
    if (r.error) return { error: r.error.message };
    return {};
  } catch (e: unknown) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

// ════════════════════════════════════════════════════════════════════
// RPC: SOCIAL (Favoriten / Blockieren) — atomar, Race-sicher (B17)
// ════════════════════════════════════════════════════════════════════

/**
 * Fügt einen Fahrer atomar zu den eigenen Favoriten hinzu (Migration 095).
 *
 * Liest, berechnet und schreibt das favoriten-jsonb-Array in EINER
 * DB-Anweisung — verhindert die Race-Condition bei schnellen Mehrfach-Klicks
 * (B17), bei der ein veralteter Client-Snapshot eine vorherige Aktion
 * überschreiben konnte. Idempotent: ein bereits vorhandener Favorit erzeugt
 * kein Duplikat.
 *
 * @param fahrerId UUID des Fahrers
 * @param name     Anzeigename (für die Favoriten-Liste)
 * @returns Das neue favoriten-Array bei Erfolg, sonst `error`.
 */
export async function addFavorit(
  fahrerId: string,
  name: string,
): Promise<{ data?: Favorit[]; error?: string }> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.rpc('add_favorit', {
      p_fahrer_id: fahrerId,
      p_name: name,
    });
    if (r.error) return { error: r.error.message };
    return { data: (r.data as Favorit[]) ?? [] };
  } catch (e: unknown) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Entfernt einen Fahrer atomar aus den eigenen Favoriten (Migration 095).
 *
 * @param fahrerId UUID des Fahrers
 * @returns Das neue favoriten-Array bei Erfolg, sonst `error`.
 */
export async function removeFavorit(
  fahrerId: string,
): Promise<{ data?: Favorit[]; error?: string }> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.rpc('remove_favorit', {
      p_fahrer_id: fahrerId,
    });
    if (r.error) return { error: r.error.message };
    return { data: (r.data as Favorit[]) ?? [] };
  } catch (e: unknown) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Fügt einen User atomar zur eigenen Blockierliste hinzu (Migration 095).
 *
 * Verhindert dieselbe Race-Condition wie addFavorit (B17). Idempotent.
 *
 * @param userId UUID des zu blockierenden Users
 * @param name   Anzeigename (für die Blockierliste)
 * @returns Das neue blocked_users-Array bei Erfolg, sonst `error`.
 */
export async function addBlocked(
  userId: string,
  name: string,
): Promise<{ data?: BlockedUser[]; error?: string }> {
  if (!_sb) return { error: NO_CLIENT_ERROR.message };
  const client = _sb;
  try {
    const r = await client.rpc('add_blocked', {
      p_user_id: userId,
      p_name: name,
    });
    if (r.error) return { error: r.error.message };
    return { data: (r.data as BlockedUser[]) ?? [] };
  } catch (e: unknown) {
    return { error: e instanceof Error ? e.message : String(e) };
  }
}

/**
 * Lädt die öffentlichen Anzeige-Felder für favorisierte Fahrer (Migration 096).
 *
 * Hintergrund: Die RLS-Policy lässt einen normalen User nur die eigene
 * Profilzeile sehen, daher sind fremde Fahrer-Profile nicht im users-Array.
 * Diese RPC liefert gezielt die öffentlichen Felder — und nur für IDs, die
 * tatsächlich in der eigenen Favoritenliste stehen (DB-seitig abgesichert).
 *
 * @param ids Liste der Fahrer-UUIDs (aus der eigenen favoriten-Liste)
 * @returns Map id → Teilprofil (firma, name, ort, plz, telefon, email)
 */
export async function getFavoritProfiles(
  ids: string[],
): Promise<{ data: Record<string, Partial<Profile>>; error?: string }> {
  if (!_sb) return { data: {}, error: NO_CLIENT_ERROR.message };
  if (ids.length === 0) return { data: {} };
  const client = _sb;
  try {
    const r = await client.rpc('get_favorit_profiles', { p_ids: ids });
    if (r.error) return { data: {}, error: r.error.message };
    const map: Record<string, Partial<Profile>> = {};
    for (const row of (r.data as Array<Record<string, unknown>>) || []) {
      const id = String(row.id);
      const firma = String(row.firma ?? '');
      const inhaber = String(row.inhaber ?? '');
      const email = String(row.email ?? '');
      map[id] = {
        id,
        firma,
        inhaber,
        // "name" wie im Mapper zusammensetzen (firma → inhaber → email),
        // da es keine echte name-Spalte gibt.
        name: firma || inhaber || email,
        ort: String(row.ort ?? ''),
        plz: String(row.plz ?? ''),
        telefon: String(row.telefon ?? ''),
        email,
      };
    }
    return { data: map };
  } catch (e: unknown) {
    return { data: {}, error: e instanceof Error ? e.message : String(e) };
  }
}
